import type { PipelineRow, PipelineSummary } from './leadPipeline';

/**
 * The outcome funnel, as arithmetic rather than as a component.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Kyu, 7 September, approving the Pipeline rework: the seven stacked band
 * tables come off the page and become one funnel you read at a glance, with a
 * single panel underneath that holds whatever you last clicked.
 *
 * This module is the reading. It computes NOTHING new: every figure here is
 * already in the `PipelineSummary` that `buildPipeline()` returns, and every
 * lead list is a filter over rows that function already produced. If this file
 * ever needs to reach past the summary for a number, the number has two
 * definitions and one of them is wrong.
 *
 * ── Why it is separate from the screen ─────────────────────────────────────
 *
 * So the percentages can be tested without rendering a table, and so "what
 * does Emailed count" has one answer rather than one per place that draws it.
 *
 * ── The one thing this fixes that the bands could not ──────────────────────
 *
 * The bands were never windowable. A lead's row records when it ARRIVED, not
 * when it entered a status, and `lead_stage_log` stores the status's display
 * name across a vocabulary rename with no key to join on. So the page carried
 * a range control that applied to the by-origin table and a printed apology
 * saying the bands below it were "right now" instead.
 *
 * Every step here is a property of the LEAD — arrived, has a touch, has a
 * reply, reached a deal — so the whole funnel honours the window, and the
 * apology goes with the bands.
 */

/** The six steps, in the order they are read. */
export type FunnelStep =
  'arrived' | 'emailed' | 'followup' | 'replied' | 'nurture' | 'deal' | 'won' | 'lost';

/**
 * Drop 143 put two STATUSES into a funnel of outcomes, on Kyu's call, and the
 * order is the argument for why that works.
 *
 * The first three and the last three are things that HAPPENED and are read off
 * activity and deals. Follow Up and Nurture are where a living lead is SITTING
 * and are read off the status somebody set. Mixing the two would be wrong if
 * the funnel were strictly nested, and it is not — Won and Lost already branch
 * off Deal and are mutually exclusive.
 *
 * What the two new steps add is the thing the funnel could never say. It
 * reported that 461 were emailed and 47 replied, and said nothing about the
 * 325 sitting in silence that nobody had decided anything about. Follow Up is
 * "somebody is still on this". Nurture is "alive, not now". The gap between
 * Emailed and those two is the pile.
 */
export const FUNNEL_ORDER: readonly FunnelStep[] = [
  'arrived',
  'emailed',
  'followup',
  'replied',
  'nurture',
  'deal',
  'won',
  'lost',
];

export const FUNNEL_LABELS: Record<FunnelStep, string> = {
  arrived: 'Arrived',
  // Was 'Emailed'. It always counted any logged touch, and once texts count
  // (20270163000100) the old word said less than the number (Kyu, 22 Sept).
  // NOT 'Contacted': that is a status, shown on this same screen, and the two
  // numbers differ (leadPipeline.test.ts, "the two vocabularies stay apart").
  emailed: 'Reached out',
  followup: 'Follow Up',
  replied: 'Replied',
  nurture: 'Nurture',
  deal: 'Deal',
  won: 'Won',
  lost: 'Lost',
};

/**
 * What each number counts, in the words that go in its ⓘ.
 *
 * Written here rather than in the component because the definition and the
 * arithmetic should not be able to drift apart. If somebody changes what
 * `emailed` selects, the sentence explaining it is on the next line.
 */
export const FUNNEL_EXPLAINERS: Record<FunnelStep, string> = {
  arrived:
    'Every lead that arrived inside the window and matches the filters above. This is the base every percentage is taken from.',
  emailed:
    'People we reached out to at least once, by email, text, call, LinkedIn message or meeting. Each person counts once, however many messages were sent. Someone who replied is counted here and in Replied. Not the same as the Contacted status, which is set by a person. Only touches LOGGED in the Hub count: the mail sync looks back ten days from when it was switched on, so an older email can be in Gmail and not here.',
  replied:
    'Leads who answered at least once. Counted from the timeline, so a reply logged by the mail sync and a reply ticked by a person count the same.',
  followup:
    'Leads a person has put on the Follow Up status: reached out to, and somebody is still on them. This is a STATUS somebody sets, not a count of activity, so it reads zero until the team uses it. The gap between Reached out and this number is the pile nobody has decided anything about.',
  nurture:
    'Leads a person has put on the Nurture status: alive and interested, but not now. Parked on purpose rather than forgotten. Also a status somebody sets. A lead here is not in Deal — the two never count the same lead.',
  deal: 'Leads that reached a deal — still open, won, or lost. It counts reaching the stage, not staying there, so this number never falls when a deal closes.',
  won: 'Leads whose deal was won. This is the only definition of conversion on this page.',
  lost: 'Leads whose deal was lost. Closed leads that never reached a deal are not here — they never entered the funnel this far.',
};

export interface FunnelLine {
  key: FunnelStep;
  label: string;
  count: number;
  /**
   * Share of ARRIVED, rounded for display.
   *
   * Always of arrived rather than of the step above. A step-over-step rate
   * reads as a conversion rate and is not one: with 12 deals from 41 replies
   * "29%" invites somebody to compare it to a win rate taken from a different
   * base. One base, said once, and the reader can do any comparison they like.
   */
  percent: number;
  /** The leads behind the number, for the panel. */
  rows: PipelineRow[];
}

/** Percentage of the base, to one decimal under 10% and whole above it. */
export function sharePercent(count: number, base: number): number {
  if (base <= 0) return 0;
  const raw = (count / base) * 100;
  // A funnel where three steps all read "0%" tells you nothing about which of
  // them is 4 leads and which is none. Under ten per cent the tenth matters.
  return raw >= 10 ? Math.round(raw) : Math.round(raw * 10) / 10;
}

function rowsOf(summary: PipelineSummary, keys: readonly string[]): PipelineRow[] {
  return summary.bands.filter((band) => keys.includes(band.key)).flatMap((band) => band.rows);
}

/** Every lead in the slice, in band order. */
export function allRows(summary: PipelineSummary): PipelineRow[] {
  return summary.bands.flatMap((band) => band.rows);
}

/**
 * The funnel.
 *
 * Counts come from the summary's own fields where it has them, and from the
 * bands where it does not — NOT from re-deriving them off the rows. The two
 * agreeing is the point: `contacted` is what the Leads tab means by emailed,
 * and a second count taken here could quietly stop matching it.
 */
export function buildFunnel(summary: PipelineSummary): FunnelLine[] {
  const rows = allRows(summary);
  const base = summary.arrived;
  const dealRows = rowsOf(summary, ['converted', 'won', 'lost']);
  const wonRows = rowsOf(summary, ['won']);
  const lostRows = rowsOf(summary, ['lost']);

  // The two status steps come straight off their own bands, which is the whole
  // reason they can be trusted: a band IS the set of leads sitting on that
  // status, so the count and the list are the same array read twice. Nothing
  // is re-derived and nothing can drift.
  const followupRows = rowsOf(summary, ['followup']);
  const nurtureRows = rowsOf(summary, ['nurture']);

  const counts: Record<FunnelStep, { count: number; rows: PipelineRow[] }> = {
    arrived: { count: summary.arrived, rows },
    emailed: { count: summary.contacted, rows: rows.filter((row) => row.contacted) },
    followup: { count: followupRows.length, rows: followupRows },
    replied: { count: summary.replied, rows: rows.filter((row) => row.replied) },
    nurture: { count: nurtureRows.length, rows: nurtureRows },
    deal: { count: summary.becameDeal, rows: dealRows },
    won: { count: wonRows.length, rows: wonRows },
    lost: { count: lostRows.length, rows: lostRows },
  };

  return FUNNEL_ORDER.map((key) => ({
    key,
    label: FUNNEL_LABELS[key],
    count: counts[key].count,
    percent: sharePercent(counts[key].count, base),
    rows: counts[key].rows,
  }));
}

/** Arrived and nobody has written to them. The one line that asks for action. */
export function neverEmailedRows(summary: PipelineSummary): PipelineRow[] {
  return allRows(summary).filter((row) => !row.contacted);
}

/**
 * Why the lost leads were lost, biggest first.
 *
 * Only leads whose deal was lost, and only those carrying a reason — a blank
 * reason is not a category called "blank", it is a lead nobody has filled in,
 * and giving it a row in this list would make it look like an answer.
 */
export function lossReasons(summary: PipelineSummary): { reason: string; count: number }[] {
  const tally = new Map<string, number>();
  for (const row of rowsOf(summary, ['lost'])) {
    const reason = row.closedBecause.trim();
    if (reason === '') continue;
    tally.set(reason, (tally.get(reason) ?? 0) + 1);
  }
  return [...tally.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}
